import type { AnySceneObjectT } from "tweenly"
import { useCallback } from "react"
import { multiselectObjectAction } from "../../store/slices/activeObject.slice"
import { useAppDispatch } from "../useRedux"

/**
 * Click handler for selecting an object.
 *
 * Deliberately thin: this runs once per CanvasItem, once per TimelineRow and once per
 * ObjectsTreeNodeMenu — several hundred instances on a large graphic. Everything the click
 * needs is read from the store inside `multiselectObjectAction`, so rendering costs nothing
 * at all here.
 *
 * It used to pull in useObjectActions, useAnimationActions, useWrapSelectedObject and
 * useWrapSelectedObjects just to have the handler ready, which meant ~25 store subscriptions
 * and two DOM-measuring computeWrapperBounds calls per instance.
 *
 * For "is this object selected", subscribe to `selectIsObjectInSelection` instead — it
 * returns a boolean, so only the components whose state really flipped re-render.
 */
const useMultiselectObject = (object: AnySceneObjectT) => {
    const dispatch = useAppDispatch()

    const handleMultiselectObject = useCallback(
        (multiSelectKey: boolean, isRootTicker?: boolean) => {
            if (!object) return
            dispatch(multiselectObjectAction(object, multiSelectKey, isRootTicker))
        },
        [dispatch, object]
    )

    return { handleMultiselectObject }
}

export default useMultiselectObject
